import type { CSSProperties } from "react";
import { resolveAssetPath } from "../shared/utilities/assets";
import type { TemplateSectionConfig } from "../types/template";

export type SectionBackgroundMode = "default" | "plain" | "photo" | "shared";
export type SectionBackgroundPosition = "center" | "top" | "bottom";
export type SectionBackgroundTone = "default" | "light" | "dark";

export interface SectionBackgroundSettings {
  mode: SectionBackgroundMode;
  image: string;
  video: string;
  color: string;
  overlay: number;
  position: SectionBackgroundPosition;
  tone: SectionBackgroundTone;
  group: string;
}

export interface SectionBackgroundGroup {
  key: string;
  mergeKey: string;
  background: SectionBackgroundSettings;
  sections: TemplateSectionConfig[];
}

const validModes = new Set<SectionBackgroundMode>(["default", "plain", "photo", "shared"]);
const validPositions = new Set<SectionBackgroundPosition>(["center", "top", "bottom"]);
const validTones = new Set<SectionBackgroundTone>(["default", "light", "dark"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function readSectionBackground(section: TemplateSectionConfig): SectionBackgroundSettings {
  const raw = isRecord(section.settings.background) ? section.settings.background : {};
  const mode = validModes.has(raw.mode as SectionBackgroundMode) ? raw.mode as SectionBackgroundMode : "default";
  const position = validPositions.has(raw.position as SectionBackgroundPosition) ? raw.position as SectionBackgroundPosition : "center";
  const tone = validTones.has(raw.tone as SectionBackgroundTone) ? raw.tone as SectionBackgroundTone : "default";
  const overlayValue = Number(raw.overlay);
  return {
    mode,
    image: typeof raw.image === "string" ? raw.image : "",
    video: typeof raw.video === "string" ? raw.video : "",
    color: typeof raw.color === "string" && /^#[0-9a-f]{6}$/i.test(raw.color) ? raw.color : "#f7f3eb",
    overlay: Number.isFinite(overlayValue) ? Math.min(0.85, Math.max(0, overlayValue)) : 0.4,
    position,
    tone,
    group: typeof raw.group === "string" ? raw.group.slice(0, 40) : ""
  };
}

function sharedBackgrounds(sections: TemplateSectionConfig[]) {
  const groups = new Map<string, SectionBackgroundSettings>();
  for (const section of sections) {
    const background = readSectionBackground(section);
    if (background.mode !== "shared" || !background.group) continue;
    const current = groups.get(background.group);
    if (!current || (!current.image && !current.video && (background.image || background.video))) groups.set(background.group, background);
  }
  return groups;
}

export function groupSectionsByBackground(sections: TemplateSectionConfig[]): SectionBackgroundGroup[] {
  const ordered = sections.slice().filter((section) => section.enabled).sort((a, b) => a.order - b.order);
  const shared = sharedBackgrounds(ordered);
  const groups: SectionBackgroundGroup[] = [];

  for (const section of ordered) {
    const ownBackground = readSectionBackground(section);
    const isShared = ownBackground.mode === "shared" && Boolean(ownBackground.group);
    const key = isShared ? `shared:${ownBackground.group}` : `section:${section.id}`;
    const previous = groups.at(-1);
    if (isShared && previous?.mergeKey === key) {
      previous.sections.push(section);
      continue;
    }
    groups.push({
      key: `${key}:${groups.length}`,
      mergeKey: key,
      background: isShared ? shared.get(ownBackground.group) || ownBackground : ownBackground,
      sections: [section]
    });
  }
  return groups;
}

export function sectionBackgroundStyle(background: SectionBackgroundSettings) {
  const source = resolveAssetPath(background.image);
  return {
    "--section-background-color": background.color,
    "--section-background-image": source ? `url("${source.replaceAll('"', '\\"')}")` : "none",
    "--section-background-overlay": String(background.overlay),
    "--section-background-position": background.position
  } as CSSProperties;
}

export function sectionBackgroundVideoSource(background: SectionBackgroundSettings) {
  if ((background.mode !== "photo" && background.mode !== "shared") || !background.video) return "";
  return resolveAssetPath(background.video);
}

export function sectionBackgroundClassName(background: SectionBackgroundSettings) {
  const custom = background.mode !== "default";
  const hasVideo = Boolean(sectionBackgroundVideoSource(background));
  return [
    "section-background-surface",
    `section-background-${background.mode}`,
    custom ? "section-background-custom" : "",
    hasVideo ? "section-background-has-video" : "",
    `section-background-tone-${background.tone}`
  ].filter(Boolean).join(" ");
}
